<script setup lang="ts">
import {computed} from "vue";
import {getRegionName} from "@/composables/functions/getRegionName";
import IconArrowDown from "@/assets/icons/IconArrowDown.vue";
import {Region} from "@/types/files";
import {RegionTopProps} from "@/types/analytics";
import {getDistrictName} from "@/composables/functions/getDistrictName";

const props = withDefaults(defineProps<RegionTopProps>(), {
  sortOrder: "desc"
});

// Сортируем данные и находим максимальное значение
const sortedData = computed(() => {
  if (!props.regionList) return [];

  const dataArray = Object.entries(props.regionList).map(([key, data]) => ({
    key: key as Region,
    value: data?.value ?? 0,
    percent: data?.percent ?? 0
  }));

  return dataArray.sort((a, b) => {
    return props.sortOrder === "desc"
        ? b.value - a.value
        : a.value - b.value;
  });
});

// Находим максимальное значение для расчета ширины баров
const maxValue = computed(() => {
  return Math.max(...sortedData.value.map(item => item.value));
});

// Функция для расчета ширины бара в процентах
const getBarWidth = (value: number) => {
  const percentage = (value / maxValue.value) * 100;
  return `${percentage}%`;
};

// Проверка положительного значения процента
const isPositivePercent = (percent: number) => {
  return percent > 0;
};
</script>

<template>
  <div class="top">
    <span class="top__title">
      <template v-if="activeType === 'russia'">
        Топ-10 регионов с {{ sortOrder === "desc" ? 'наибольшими' : 'наименьшими' }} показателями
      </template>
      <template v-else-if="activeType === 'district'">
        Топ регионов по {{ getDistrictName(selectedDistrict) }} с {{ sortOrder === "desc" ? 'наибольшими' : 'наименьшими' }} показателями
      </template>
    </span>

    <div class="top-chart">
      <div
        v-for="item of sortedData"
        :key="item.key"
        class="top-chart__item"
      >
        <span class="top-chart__label">
          {{ getRegionName(item.key) }}
        </span>
        <div class="top-chart__bar-wrapper">
          <div
            class="top-chart__bar"
            :class="{ 'top-chart__bar--positive': isPositivePercent(item.percent) }"
            :style="{ width: getBarWidth(item.value) }"
          >
            <span class="top-chart__percent">
              <IconArrowDown
                class="top-chart__icon"
                :class="{'top-chart__icon--positive': isPositivePercent(item.percent)}"
              />
              {{ item.percent }}%
            </span>
          </div>
          <span class="top-chart__value">
            {{ item.value }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.top {
  display: flex;
  flex-direction: column;
  gap: 16px;

  background-color: var(--background-bg-white);
  box-shadow: 1px 2px 10px 0 var(--gray-gray50);

  padding: 20px;
  border-radius: 8px;

  &__title {
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
  }
}

.top-chart {
  display: flex;
  flex-direction: column;
  gap: 10px;

  &__icon {
    width: 12px;
    height: 12px;

    &--positive {
      transform: rotate(180deg);
    }
  }

  &__item {
    display: grid;
    grid-template-columns: 95px 1fr;
    align-items: center;
    gap: 12px;
  }

  &__label {
    font-size: 12px;
    line-height: 20px;
    color: #333;
    width: 95px;
    text-align: right;
  }

  &__bar-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__bar {
    background-color: var(--oracul-red-red30);
    border-radius: 4px;
    padding: 4px 12px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    height: 36px;
    min-width: 80px;
    transition: width 0.3s ease;

    &--positive {
      background-color: var(--oracul-green-green40);
    }
  }

  &__percent {
    display: flex;
    align-items: center;
    gap: 2px;

    color: white;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
  }

  &__value {
    font-size: 14px;
    font-weight: 500;
    color: var(--oracul-texticon-black-text);
    line-height: 10px;
  }
}
</style>